// EmailOTPForm Component
// UPDATED: Accepts isSignUp prop for different messaging
// UPDATED: Added rate limiting for security
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { otpRateLimiter } from '../../utils/rateLimiter';

const EmailOTPForm = ({ onSuccess, onError, isSignUp = false }) => {
  const { sendOTP, verifyOTP } = useAuth();
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [step, setStep] = useState('email'); // 'email' or 'otp'
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [resendTimer, setResendTimer] = useState(0);

  // Countdown timer for resend
  useEffect(() => {
    let interval;
    if (resendTimer > 0) {
      interval = setInterval(() => {
        setResendTimer((prev) => prev - 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [resendTimer]);

  const handleSendOTP = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    // Check rate limit before sending
    const { limited, retryAfter } = otpRateLimiter.isRateLimited(email);
    if (limited) {
      setError(`Too many attempts. Please try again in ${retryAfter} seconds.`);
      return;
    }

    setLoading(true);

    // Record the attempt
    otpRateLimiter.recordAttempt(email);

    try {
      const result = await sendOTP(email);
      if (result.success) {
        setStep('otp');
        setSuccess('Verification link sent! Check your email inbox.');
        setResendTimer(60); // 60 second cooldown
      } else {
        setError(result.error || 'Failed to send verification email');
      }
    } catch (err) {
      setError('Something went wrong. Please try again.');
      onError?.(err);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      const result = await verifyOTP(email, otp);
      if (result.success) {
        setSuccess('Verified successfully!');
        onSuccess?.();
      } else {
        setError(result.error || 'Invalid or expired code');
      }
    } catch (err) {
      setError('Verification failed. Please try again.');
      onError?.(err);
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (resendTimer > 0) return;

    // Check rate limit before resending
    const { limited, retryAfter } = otpRateLimiter.isRateLimited(email);
    if (limited) {
      setError(`Too many attempts. Please try again in ${retryAfter} seconds.`);
      return;
    }

    setError('');
    setLoading(true);

    // Record the attempt
    otpRateLimiter.recordAttempt(email);

    try {
      const result = await sendOTP(email);
      if (result.success) {
        setSuccess('New verification link sent!');
        setResendTimer(60);
      } else {
        setError(result.error || 'Failed to resend');
      }
    } catch (err) {
      setError('Failed to resend. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleChangeEmail = () => {
    setStep('email');
    setOtp('');
    setError('');
    setSuccess('');
  };

  return (
    <div className="email-otp-form">
      {step === 'email' ? (
        <form onSubmit={handleSendOTP}>
          <div className="form-group">
            <label htmlFor="email">Email address</label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              required
              disabled={loading}
              autoFocus
            />
          </div>

          {error && <div className="error-message">{error}</div>}

          <button type="submit" className="submit-button" disabled={loading || !email}>
            {loading ? 'Sending...' : isSignUp ? 'Create Account' : 'Send Verification Link'}
          </button>

          <p className="form-hint">
            {isSignUp
              ? "We'll send you a magic link to create your account instantly"
              : "We'll send you a magic link to sign in securely"}
          </p>
        </form>
      ) : (
        <form onSubmit={handleVerifyOTP}>
          <div className="email-display">
            <span>
              Sent to <strong>{email}</strong>
            </span>
            <button type="button" className="change-email" onClick={handleChangeEmail}>
              Change
            </button>
          </div>

          {success && <div className="success-message">{success}</div>}
          {error && <div className="error-message">{error}</div>}

          <div className="verification-instructions">
            <p className="form-hint">
              <strong>Check your email!</strong> Click the link we sent to{' '}
              {isSignUp ? 'complete signup' : 'sign in'}.
            </p>
            <p
              className="form-hint"
              style={{ marginTop: '0.5rem', fontSize: '0.8rem', color: '#9ca3af' }}
            >
              The link will open in a new tab and sign you in automatically.
            </p>
          </div>

          <div className="resend-container">
            {resendTimer > 0 ? (
              <span className="resend-timer">Resend available in {resendTimer}s</span>
            ) : (
              <button
                type="button"
                className="resend-button"
                onClick={handleResend}
                disabled={loading}
              >
                Didn't receive it? Resend link
              </button>
            )}
          </div>
        </form>
      )}
    </div>
  );
};

export default EmailOTPForm;
